import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { LoggerEvent, LoggerProvider } from '../logger/logger';

declare var talsec: any;

export type TalsecThreat =
  | 'privilegedAccess'
  | 'debug'
  | 'simulator'
  | 'appIntegrity'
  | 'unofficialStore'
  | 'hooks'
  | 'deviceBinding'
  | 'deviceID'
  | 'passcode'
  | 'overlay'
  | 'secureHardwareNotAvailable'
  | 'obfuscationIssues'
  | 'devMode'
  | 'systemVPN'
  | 'malware'
  | 'adbEnabled'
  | 'screenshot'
  | 'screenRecording'
  | 'multiInstance'
  | 'timeSpoofing'
  | 'locationSpoofing'
  | 'unsecureWifi'
  | 'automation';

// The native check runs per input event, so one tap reports twice. One line a second is enough.
const OVERLAY_LOG_INTERVAL_MS = 1000;

@Injectable()
export class TalsecProvider {
  private initialized = false;
  private allChecksFinished = false;
  private lastOverlayLogAt = 0;

  constructor(private platform: Platform) {}

  async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    try {
      await this.platform.ready();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.RootDetection, 'Talsec: platform not ready');
      return;
    }

    if (typeof talsec === 'undefined' || !talsec.registerListener) {
      LoggerProvider.warning(this, LoggerEvent.RootDetection, 'Talsec: plugin not available on this platform');
      return;
    }

    try {
      talsec.registerListener(this.buildThreatActions());
      talsec.registerRaspExecutionStateListener({
        allChecksFinished: () => {
          this.allChecksFinished = true;
          LoggerProvider.info(this, 'Talsec: all checks finished');
        },
      });
      LoggerProvider.info(this, 'Talsec: listeners registered');
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.RootDetection, 'Talsec: failed to register listeners');
    }
  }

  isReady(): boolean {
    return this.allChecksFinished;
  }

  async blockScreenCapture(enable: boolean): Promise<void> {
    if (typeof talsec === 'undefined' || !talsec.blockScreenCapture) {
      return;
    }
    try {
      await talsec.blockScreenCapture(enable);
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.RootDetection, `Talsec: blockScreenCapture(${enable}) failed`);
    }
  }

  async isScreenCaptureBlocked(): Promise<boolean> {
    if (typeof talsec === 'undefined' || !talsec.isScreenCaptureBlocked) {
      return false;
    }
    try {
      return await talsec.isScreenCaptureBlocked();
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.RootDetection, 'Talsec: isScreenCaptureBlocked failed');
      return false;
    }
  }

  private buildThreatActions(): { [k: string]: (payload?: any) => void } {
    const log = (threat: TalsecThreat, details?: any) => this.handleThreat(threat, details);
    return {
      privilegedAccess: () => log('privilegedAccess'),
      debug: () => log('debug'),
      simulator: () => log('simulator'),
      appIntegrity: () => log('appIntegrity'),
      unofficialStore: () => log('unofficialStore'),
      hooks: () => log('hooks'),
      deviceBinding: () => log('deviceBinding'),
      deviceID: () => log('deviceID'),
      passcode: () => log('passcode'),
      overlay: () => log('overlay'),
      secureHardwareNotAvailable: () => log('secureHardwareNotAvailable'),
      obfuscationIssues: () => log('obfuscationIssues'),
      devMode: () => log('devMode'),
      systemVPN: () => log('systemVPN'),
      malware: (suspiciousApps: any[]) => log('malware', { suspiciousApps }),
      adbEnabled: () => log('adbEnabled'),
      screenshot: () => log('screenshot'),
      screenRecording: () => log('screenRecording'),
      multiInstance: () => log('multiInstance'),
      timeSpoofing: () => log('timeSpoofing'),
      locationSpoofing: () => log('locationSpoofing'),
      unsecureWifi: () => log('unsecureWifi'),
      automation: () => log('automation'),
    };
  }

  private handleThreat(threat: TalsecThreat, details?: any): void {
    if (threat === 'overlay' && !this.shouldLogOverlay()) {
      return;
    }

    const severity = this.severityFor(threat);
    const message = `Talsec threat: ${threat}`;
    switch (severity) {
      case 'critical':
        LoggerProvider.critical(this, LoggerEvent.RootDetection, message, details);
        break;
      case 'error':
        LoggerProvider.error(this, LoggerEvent.RootDetection, message, details);
        break;
      default:
        LoggerProvider.warning(this, LoggerEvent.RootDetection, message, details);
    }
  }

  // performance.now(), not Date.now(): a backwards clock jump would stall the throttle for
  // as long as the jump, and timeSpoofing is a threat this very provider reports.
  private shouldLogOverlay(): boolean {
    const now = performance.now();
    if (now - this.lastOverlayLogAt < OVERLAY_LOG_INTERVAL_MS) {
      return false;
    }
    this.lastOverlayLogAt = now;
    return true;
  }

  private severityFor(threat: TalsecThreat): 'critical' | 'error' | 'warning' {
    switch (threat) {
      case 'privilegedAccess':
      case 'hooks':
      case 'appIntegrity':
      case 'malware':
        return 'critical';
      case 'debug':
      case 'simulator':
      case 'unofficialStore':
      case 'deviceBinding':
      case 'devMode':
      case 'adbEnabled':
      case 'automation':
      case 'multiInstance':
        return 'error';
      default:
        return 'warning';
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/talsec/talsec.ts